import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { ActivityItem } from './activity-item';
export { ActivityItem };
import { activityItemSchema } from '@schemas/business-objects/activity-item';
import { ActivityFormGroup } from './activity-form-group';
import { ResourceSetFormGroup } from './resource-set-form-group';
import { SessionActivityItemFormArray } from './session-activity-item-form-group';
import type { Resource } from './resource';
import type { TextBox } from './text-box';
import type { User } from './user';

export class ActivityItemFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    type: SchemaFormControl;
    required: SchemaFormControl;
    validValues: SchemaFormControl;
    sequence: SchemaFormControl;
    locked: SchemaFormControl;
    activityId: SchemaFormControl;
    activity: ActivityFormGroup;
    resourceSetId: SchemaFormControl;
    resourceSet: ResourceSetFormGroup;
    sessionActivityItems: SessionActivityItemFormArray;
  };

  declare value: Partial<ActivityItem>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(activityItemSchema, ActivityItem, data, options);
  }

  get object(): ActivityItem {
    return new ActivityItem(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'activity':
        return new ActivityFormGroup();
      case 'resourceSet':
        return new ResourceSetFormGroup();
      case 'sessionActivityItems':
        return new SessionActivityItemFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<ActivityItem[]> {
    return ActivityItem.find(filter);
  }

  static async findOne(filter?: any): Promise<ActivityItem | null> {
    return ActivityItem.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<ActivityItem> {
    return ActivityItem.findById(id, filter);
  }

  static async create(data: Partial<ActivityItem>): Promise<ActivityItem> {
    return ActivityItem.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<ActivityItem>,
  ): Promise<ActivityItem> {
    return ActivityItem.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return ActivityItem.deleteById(id);
  }

  async dataForCopy(): Promise<any> {
    const instance = new ActivityItem(this.getRawValue());
    return instance.dataForCopy();
  }
}

export class ActivityItemFormArray extends PersistedFormArray {
  readonly entityName = 'ActivityItem' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(activityItemSchema, ActivityItem, options);
  }

  protected override createItemGroup() {
    return new ActivityItemFormGroup();
  }

  get array(): ActivityItem[] {
    return this.controls.map((group: ActivityItemFormGroup) => group.object);
  }
}

export class ActivityItemPersistedArray extends PersistedArray<ActivityItem> {
  readonly entityName = 'ActivityItem' as const;

  constructor(options?: PersistedArrayOptions) {
    super(ActivityItem, options);
  }
}
